import { useState, useCallback, useRef } from 'react';
import Map, { Source, Layer, NavigationControl, Popup } from 'react-map-gl/maplibre';
import type { MapLayerMouseEvent, MapRef } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';
import { authenticatedMapRequest, INITIAL_VIEW, MAP_STYLE, territoryTileUrl } from '@/config';
import { tenure } from '@/styles/theme';
import { useMapRecenter } from '@/hooks/useMapRecenter';
import { DataAttribution } from '@/components/map/DataAttribution';
import { useTerritoryStore } from '@/stores/territoryStore';

/**
 * RenovationMap — carte du parc a renover (Strategie passoires).
 * Croise 3 couches sur la maille 200 m + marque le bati prioritaire (copro F/G, parc social, communal).
 *
 * Couche active (single-select) :
 *   priority   = Passoires x Precarite x Gisement -> liste hierarchisee
 *   passoires  = concentration DPE F/G (BDNB)
 *   precarite  = bas revenus x taux d'effort energetique (INSEE Filosofi)
 *   gisement   = potentiel mobilisable (saut de classe, copro/social, eligibilite aides)
 */
export type RenoLayer = 'priority' | 'passoires' | 'precarite' | 'gisement';

const LAYER_CHIPS: { id: RenoLayer; label: string }[] = [
  { id: 'priority', label: 'Priorite' },
  { id: 'passoires', label: 'Passoires F/G' },
  { id: 'precarite', label: 'Precarite' },
  { id: 'gisement', label: 'Gisement' },
];

const RAMP: Record<RenoLayer, unknown[]> = {
  priority: ['interpolate', ['linear'], ['get', 'priority'], 0, '#eaf2fb', 0.4, '#9bbce6', 0.7, '#3b82f6', 0.88, '#1d4ed8', 1, '#11235e'],
  passoires: ['interpolate', ['linear'], ['get', 'passoire_ratio'], 0, '#fff3df', 0.5, '#ef7d18', 1, '#e30613'],
  precarite: ['interpolate', ['linear'], ['get', 'precarite'], 0, '#efe7f7', 0.5, '#b79bd8', 1, '#6b3fa0'],
  gisement: ['interpolate', ['linear'], ['get', 'gisement'], 0, '#e6f4ee', 0.5, '#5cbd92', 1, '#1f8a5b'],
};

const LEGEND: Record<RenoLayer, { title: string; lo: string; hi: string; loColor: string; hiColor: string }> = {
  priority:  { title: 'Indice de priorite renovation',  lo: 'faible', hi: 'critique', loColor: '#eaf2fb', hiColor: '#11235e' },
  passoires: { title: 'Concentration passoires F/G',    lo: '0 %',    hi: '> 40 %',  loColor: '#fff3df', hiColor: '#e30613' },
  precarite: { title: 'Precarite energetique',          lo: 'faible', hi: 'forte',    loColor: '#efe7f7', hiColor: '#6b3fa0' },
  gisement:  { title: 'Gisement mobilisable',           lo: 'faible', hi: 'eleve',   loColor: '#e6f4ee', hiColor: '#1f8a5b' },
};

function tooltipText(layer: RenoLayer, props: Record<string, number>): string {
  if (layer === 'passoires') return `${Math.round(props.passoire_ratio * 100)} % F/G`;
  if (layer === 'precarite') return `precarite ${Math.round(props.precarite * 100)}/100`;
  if (layer === 'gisement')  return `gisement ${Math.round(props.gisement * 100)}/100`;
  return `priorite ${Math.round(props.priority * 100)}/100`;
}

interface RenovationMapProps {
  layer: RenoLayer;
  onLayerChange: (l: RenoLayer) => void;
  showAfter?: boolean;
  onShowAfterChange?: (v: boolean) => void;
  simDone?: boolean;
}

export function RenovationMap({
  layer,
  onLayerChange,
  showAfter = false,
  onShowAfterChange,
  simDone = false,
}: RenovationMapProps) {
  const [hover, setHover] = useState<{ lng: number; lat: number; text: string } | null>(null);
  const territoryCode = useTerritoryStore((s) => s.activeTerritory?.code_insee);
  const mapRef = useRef<MapRef>(null);
  const [mapReady, setMapReady] = useState(false);
  useMapRecenter(mapRef, mapReady);

  const onHover = useCallback(
    (e: MapLayerMouseEvent) => {
      const f = e.features?.[0];
      setHover(
        f
          ? {
              lng: e.lngLat.lng,
              lat: e.lngLat.lat,
              text: tooltipText(layer, f.properties as Record<string, number>),
            }
          : null,
      );
    },
    [layer],
  );

  const fillPaint = {
    'fill-color': RAMP[layer] as never,
    'fill-opacity': 0.62,
    'fill-outline-color': 'rgba(255,255,255,0.32)',
  };

  // Apres simulation : marqueurs verts (logements cibles par la campagne)
  const buildingStrokeColor = (showAfter && simDone
    ? '#1f8a5b'
    : ['match', ['get', 'tenure'], 'copro', tenure.copro, 'social', tenure.social, 'communal', tenure.communal, '#5b6480']
  ) as never;

  const legend = LEGEND[layer];

  if (!territoryCode) return null;

  return (
    <div className="flex flex-col gap-2 h-full">
      {/* Layer chips + toggle Avant/Apres */}
      <div className="flex items-center gap-1.5 flex-wrap">
        {LAYER_CHIPS.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => onLayerChange(c.id)}
            className={`rounded-full px-3 py-1 text-[12px] font-semibold border transition ${
              layer === c.id
                ? 'bg-ink text-white border-ink'
                : 'bg-white text-muted border-line hover:border-ink hover:text-ink'
            }`}
          >
            {c.label}
          </button>
        ))}
        {onShowAfterChange && (
          <div className="ml-auto flex rounded-[9px] border border-line overflow-hidden text-[12px] font-semibold">
            <button
              type="button"
              onClick={() => onShowAfterChange(false)}
              className={`px-3 py-1 transition ${!showAfter ? 'bg-ink text-white' : 'bg-white text-muted hover:text-ink'}`}
            >
              Avant
            </button>
            <button
              type="button"
              disabled={!simDone}
              onClick={() => onShowAfterChange(true)}
              className={`px-3 py-1 transition ${showAfter ? 'bg-[#1f8a5b] text-white' : 'bg-white text-muted hover:text-ink'} disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              Apres
            </button>
          </div>
        )}
      </div>

      {/* Carte */}
      <div className="relative flex-1 min-h-0 overflow-hidden rounded-panel border border-line">
        <Map
          ref={mapRef}
          initialViewState={INITIAL_VIEW}
          mapStyle={MAP_STYLE}
          transformRequest={authenticatedMapRequest}
          style={{ width: '100%', height: '100%' }}
          interactiveLayerIds={['reno-fill']}
          onLoad={() => setMapReady(true)}
          onMouseMove={onHover}
        >
          <NavigationControl position="top-right" showCompass={false} />
          <DataAttribution />

          {/* Choroplethe couche active */}
          <Source
            id="reno"
            type="vector"
            tiles={[territoryTileUrl(territoryCode, 'renovation_grid')]}
            minzoom={10}
            maxzoom={16}
          >
            <Layer id="reno-fill" type="fill" source-layer="renovation_grid" paint={fillPaint} />
          </Source>

          {/* Bati prioritaire a enroler (copro F/G, parc social, communal) */}
          <Source
            id="prio-build"
            type="vector"
            tiles={[territoryTileUrl(territoryCode, 'priority_buildings')]}
            minzoom={12}
            maxzoom={18}
          >
            <Layer
              id="prio-build-pt"
              type="circle"
              source-layer="priority_buildings"
              paint={{
                'circle-radius': 6,
                'circle-color': '#ffffff',
                'circle-stroke-width': 2.5,
                'circle-stroke-color': buildingStrokeColor,
              }}
            />
          </Source>

          {hover && (
            <Popup longitude={hover.lng} latitude={hover.lat} closeButton={false} offset={12}>
              <div className="font-sans text-xs">
                <div className="font-bold text-ink">{legend.title}</div>
                <div className="font-mono text-muted">{hover.text}</div>
              </div>
            </Popup>
          )}
        </Map>
      </div>

      {/* Legende dynamique */}
      <div className="flex items-center gap-2 px-1">
        <span className="h-2.5 w-2.5 rounded-sm flex-shrink-0" style={{ background: legend.loColor }} />
        <span className="text-[11px] text-muted">{legend.lo}</span>
        <div
          className="flex-1 h-[5px] rounded-full"
          style={{ background: `linear-gradient(to right, ${legend.loColor}, ${legend.hiColor})` }}
        />
        <span className="text-[11px] text-muted">{legend.hi}</span>
        <span className="h-2.5 w-2.5 rounded-sm flex-shrink-0" style={{ background: legend.hiColor }} />
      </div>

      {/* Symbologie occupation */}
      <div className="flex items-center gap-4 px-1 text-[11px] text-muted flex-wrap">
        <span className="flex items-center gap-1.5">
          <span
            className="inline-block h-3 w-3 rounded-full border-2 bg-white"
            style={{ borderColor: tenure.copro }}
            aria-hidden="true"
          />
          Copropriete
        </span>
        <span className="flex items-center gap-1.5">
          <span
            className="inline-block h-3 w-3 rounded-full border-2 bg-white"
            style={{ borderColor: tenure.social }}
            aria-hidden="true"
          />
          Parc social
        </span>
        <span className="flex items-center gap-1.5">
          <span
            className="inline-block h-3 w-3 rounded-full border-2 bg-white"
            style={{ borderColor: tenure.communal }}
            aria-hidden="true"
          />
          Bati communal
        </span>
        {showAfter && simDone && (
          <span className="flex items-center gap-1.5">
            <span
              className="inline-block h-3 w-3 rounded-full border-2 bg-white"
              style={{ borderColor: '#1f8a5b' }}
              aria-hidden="true"
            />
            Cibles campagne
          </span>
        )}
      </div>
    </div>
  );
}
